import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../API';
import { extractData } from '../../utils/apiResponse';
import HelpPopover from '../blocks/HelpPopover';
import styles from './FilenamePatternField.module.css';

const TEST_DEBOUNCE_MS = 400;
const MAX_CASE_FILENAMES = 300;

const HELP = (
    <>
        <p>
            When a slide is added, its lab filename is checked against these patterns.
            If exactly one block or stain matches and that field is still empty, it can
            be assigned automatically.
        </p>
        <p>
            Patterns are{' '}
            <a href="https://regexr.com/" target="_blank" rel="noopener noreferrer">
                regular expressions
            </a>
            {' '}matched against the filename only (not the path), ignoring letter case.
            Leave blank to skip auto-matching; use Test… to try a pattern with real filenames.
        </p>
    </>
);

function fileBasename(path) {
    if (path == null || path === '') return '';
    const normalized = String(path).replace(/\\/g, '/');
    const parts = normalized.split('/');
    return parts[parts.length - 1] || normalized;
}

function filenamesFromCaseDoc(caseDoc) {
    const items = Array.isArray(caseDoc?.items) ? caseDoc.items : [];
    const seen = new Set();
    const out = [];
    for (const item of items) {
        const raw = item?.bdsaData?.originalFilename || item?.name;
        const name = fileBasename(raw);
        if (!name) continue;
        const key = name.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(name);
        if (out.length >= MAX_CASE_FILENAMES) break;
    }
    return out;
}

function appendSampleNames(prev, names) {
    const existing = new Set(
        String(prev || '')
            .split('\n')
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean),
    );
    const toAdd = names.filter((n) => !existing.has(String(n).toLowerCase()));
    if (toAdd.length === 0) {
        return { next: prev, added: 0 };
    }
    const base = String(prev || '').trim() ? `${String(prev).trimEnd()}\n` : '';
    return { next: `${base}${toAdd.join('\n')}`, added: toAdd.length };
}

function sortCases(list) {
    return [...list].sort((a, b) => {
        const aId = a?.networkIdentifier || a?.localIdentifier || '';
        const bId = b?.networkIdentifier || b?.localIdentifier || '';
        return String(aId).localeCompare(String(bId), undefined, { numeric: true, sensitivity: 'base' });
    });
}

/**
 * Optional filename-pattern editor with live tester and case-filename loader.
 * Pattern input stays visible; sample testing expands behind Test….
 * @param {'section'|'sheet'} variant - section = wizard card chrome; sheet = Block name label/input look
 */
export default function FilenamePatternField({
    value = '',
    onChange,
    disabled = false,
    onSave,
    savePending = false,
    showSaveButton = false,
    dirty = false,
    variant = 'section',
    currentCaseNetworkId = null,
}) {
    const uid = useId();
    const patternId = `${uid}-pattern`;
    const samplesId = `${uid}-samples`;

    const [testerOpen, setTesterOpen] = useState(false);
    const [samples, setSamples] = useState('');
    const [testState, setTestState] = useState({ loading: false, error: null, results: [], patternValid: null });
    const [pickerOpen, setPickerOpen] = useState(false);
    const [caseFilter, setCaseFilter] = useState('');
    const [loadStatus, setLoadStatus] = useState(null);
    const [loadPending, setLoadPending] = useState(false);

    const { data: caseListResponse, isLoading: casesLoading } = useQuery({
        queryKey: ['bdsa-case-list'],
        queryFn: () => api.get('bdsa/case'),
        staleTime: 5 * 60 * 1000,
        enabled: testerOpen && pickerOpen,
    });

    const cases = useMemo(() => {
        const list = extractData(caseListResponse);
        return sortCases(Array.isArray(list) ? list : []);
    }, [caseListResponse]);

    const filteredCases = useMemo(() => {
        const q = caseFilter.trim().toLowerCase();
        if (!q) return cases;
        return cases.filter((c) => {
            const hay = [c.networkIdentifier, c.localIdentifier]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [cases, caseFilter]);

    const sampleList = useMemo(
        () => samples.split('\n').map((s) => s.trim()).filter(Boolean),
        [samples],
    );

    const runTest = useCallback(async (pattern, filenames) => {
        const trimmed = (pattern || '').trim();
        if (!trimmed || filenames.length === 0) {
            setTestState({ loading: false, error: null, results: [], patternValid: null });
            return;
        }
        setTestState((prev) => ({ ...prev, loading: true, error: null }));
        try {
            const resp = await api.post('/bdsa/filename-pattern/test', {
                body: { pattern: trimmed, filenames },
            });
            if (resp.status === 200 && resp.data) {
                setTestState({
                    loading: false,
                    error: resp.data.patternValid ? null : (resp.data.patternError || 'Invalid pattern'),
                    results: resp.data.results || [],
                    patternValid: resp.data.patternValid,
                });
            } else {
                setTestState({ loading: false, error: 'Test request failed', results: [], patternValid: false });
            }
        } catch (err) {
            setTestState({
                loading: false,
                error: err?.message || 'Test request failed',
                results: [],
                patternValid: false,
            });
        }
    }, []);

    useEffect(() => {
        if (!testerOpen) {
            return undefined;
        }
        if (sampleList.length === 0) {
            setTestState({ loading: false, error: null, results: [], patternValid: null });
            return undefined;
        }
        const handle = window.setTimeout(() => {
            runTest(value, sampleList);
        }, TEST_DEBOUNCE_MS);
        return () => window.clearTimeout(handle);
    }, [testerOpen, value, sampleList, runTest]);

    const applyFilenamesFromCase = useCallback(async (networkIdentifier) => {
        if (!networkIdentifier || disabled) return;
        setLoadPending(true);
        setLoadStatus(null);
        try {
            const resp = await api.get('bdsa/case', { query: { networkIdentifier } });
            if (resp.status !== 200 || !resp.data) {
                setLoadStatus({ tone: 'error', text: 'Could not load that case.' });
                return;
            }
            const names = filenamesFromCaseDoc(resp.data);
            if (names.length === 0) {
                setLoadStatus({ tone: 'muted', text: 'No slides on that case.' });
                return;
            }
            let added = 0;
            setSamples((prev) => {
                const result = appendSampleNames(prev, names);
                added = result.added;
                return result.next;
            });
            setLoadStatus({
                tone: 'ok',
                text: added > 0
                    ? `Added ${added} name${added === 1 ? '' : 's'} from ${networkIdentifier}`
                    : `All names from ${networkIdentifier} were already listed`,
            });
            setPickerOpen(false);
            setCaseFilter('');
        } catch (err) {
            setLoadStatus({ tone: 'error', text: err?.message || 'Could not load that case.' });
        } finally {
            setLoadPending(false);
        }
    }, [disabled]);

    const isSheet = variant === 'sheet';
    const labelClass = isSheet ? styles.sheetLabel : styles.fieldLabel;
    const inputClass = isSheet ? styles.sheetInput : styles.patternInput;

    const body = (
        <>
            <div className={styles.labelRow}>
                <label className={labelClass} htmlFor={patternId}>
                    {isSheet ? 'Filename pattern (optional)' : 'Pattern'}
                </label>
                <HelpPopover title="Filename patterns">{HELP}</HelpPopover>
            </div>
            <input
                id={patternId}
                type="text"
                className={inputClass}
                value={value || ''}
                onChange={(e) => onChange?.(e.target.value)}
                placeholder="e.g. _he\b or hipp|ent"
                disabled={disabled}
                spellCheck={false}
            />
            <div className={styles.actionsRow}>
                {showSaveButton && onSave && (
                    <button
                        type="button"
                        className={styles.saveButton}
                        onClick={onSave}
                        disabled={disabled || !dirty || savePending}
                    >
                        {savePending ? 'Saving…' : 'Save pattern'}
                    </button>
                )}
                <button
                    type="button"
                    className={styles.testToggle}
                    onClick={() => {
                        setTesterOpen((open) => !open);
                        if (testerOpen) {
                            setPickerOpen(false);
                        }
                    }}
                    disabled={disabled}
                    aria-expanded={testerOpen}
                >
                    {testerOpen ? 'Hide test' : 'Test…'}
                </button>
                {showSaveButton && dirty && (
                    <span className={styles.dirtyHint} role="status">Unsaved pattern</span>
                )}
            </div>

            {testerOpen && (
                <div className={styles.testerPanel}>
                    <label className={styles.samplesLabel} htmlFor={samplesId}>
                        Test against sample filenames (one per line)
                    </label>
                    <textarea
                        id={samplesId}
                        className={styles.samples}
                        value={samples}
                        onChange={(e) => setSamples(e.target.value)}
                        placeholder={'slide_001_he.svs\n/path/to/block_a_01.svs'}
                        disabled={disabled}
                    />
                    <div className={styles.loadRow}>
                        <button
                            type="button"
                            className={styles.loadButton}
                            onClick={() => {
                                setPickerOpen((open) => !open);
                                setLoadStatus(null);
                            }}
                            disabled={disabled || loadPending}
                        >
                            Load from case…
                        </button>
                        {currentCaseNetworkId && (
                            <button
                                type="button"
                                className={styles.loadButton}
                                onClick={() => applyFilenamesFromCase(currentCaseNetworkId)}
                                disabled={disabled || loadPending}
                            >
                                Use this case’s filenames
                            </button>
                        )}
                    </div>
                    {pickerOpen && (
                        <div className={styles.casePicker} role="dialog" aria-label="Choose a case">
                            <input
                                type="search"
                                className={styles.caseFilter}
                                value={caseFilter}
                                onChange={(e) => setCaseFilter(e.target.value)}
                                placeholder="Filter cases…"
                                disabled={disabled || loadPending}
                                autoFocus
                            />
                            <div className={styles.caseList}>
                                {casesLoading && <p className={styles.loadHint}>Loading cases…</p>}
                                {!casesLoading && filteredCases.length === 0 && (
                                    <p className={styles.loadHint}>No cases found.</p>
                                )}
                                {!casesLoading && filteredCases.map((c) => {
                                    const id = c.networkIdentifier || c.localIdentifier;
                                    if (!id) return null;
                                    return (
                                        <button
                                            key={id}
                                            type="button"
                                            className={styles.caseOption}
                                            onClick={() => applyFilenamesFromCase(id)}
                                            disabled={disabled || loadPending}
                                        >
                                            <span className={styles.caseId}>{c.networkIdentifier || id}</span>
                                            {c.localIdentifier && c.localIdentifier !== c.networkIdentifier && (
                                                <span className={styles.caseLocal}>{c.localIdentifier}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {loadPending && <p className={styles.testing}>Loading filenames…</p>}
                    {loadStatus && (
                        <p
                            className={
                                loadStatus.tone === 'error'
                                    ? styles.error
                                    : loadStatus.tone === 'ok'
                                        ? styles.loadOk
                                        : styles.loadHint
                            }
                            role="status"
                        >
                            {loadStatus.text}
                        </p>
                    )}
                    {testState.loading && <p className={styles.testing}>Testing…</p>}
                    {testState.error && <p className={styles.error} role="alert">{testState.error}</p>}
                    {testState.results.length > 0 && (
                        <div className={styles.results}>
                            <div className={styles.resultsHeader}>
                                <span>Filename</span>
                                <span>Matched</span>
                            </div>
                            {testState.results.map((row) => (
                                <div key={row.filename} className={styles.resultsRow}>
                                    <span>{row.filename}</span>
                                    <span className={row.matched ? styles.matchYes : styles.matchNo}>
                                        {row.matched ? 'Yes' : 'No'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className={styles.hint}>Only the basename is matched; paths and extension case are normalized.</p>
                </div>
            )}
        </>
    );

    if (isSheet) {
        return (
            <div className={styles.sheet}>
                {body}
            </div>
        );
    }

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <div>
                    <h4>Filename pattern (optional)</h4>
                    <small>Optional regex for auto-assigning slides from the filename.</small>
                </div>
            </div>
            <div className={styles.sectionContent}>
                {body}
            </div>
        </div>
    );
}
